import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectTemplatesSearch, templatesSearchChanged } from '../../store/templatesSlice';

const SEARCH_DEBOUNCE_MS = 300;

export function useTemplatesSearchField() {
  const dispatch = useDispatch();
  const search = useSelector(selectTemplatesSearch);
  const [value, setValueState] = useState(search);
  const timerRef = useRef(null);

  useEffect(() => {
    setValueState(search);
  }, [search]);

  const cancelPending = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  useEffect(() => cancelPending, [cancelPending]);

  // The input updates on every keystroke; the store (and so the list/facets requests) only
  // once typing pauses.
  const setValue = useCallback(
    (next) => {
      const nextStr = next == null ? '' : String(next);
      setValueState(nextStr);
      cancelPending();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        dispatch(templatesSearchChanged(nextStr));
      }, SEARCH_DEBOUNCE_MS);
    },
    [dispatch, cancelPending],
  );

  const clear = useCallback(() => {
    cancelPending();
    setValueState('');
    dispatch(templatesSearchChanged(''));
  }, [dispatch, cancelPending]);

  const isSearchActive = String(search ?? '').trim().length > 0;

  return {
    value,
    setValue,
    clear,
    isSearchActive,
    search,
  };
}
